import React, { useState } from "react";
import { Search, Filter, ArrowUpRight, CheckCircle2, Clock, XCircle, Bell, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ProfileOverlay } from "../components/ProfileOverlay";
import { useAuth } from "../context/AuthContext";
import { useMe } from "../hooks/useMe";
import { useNotifications } from "../hooks/useNotifications";

const STATUS_DISPLAY = {
  completed: {
    label: "Delivered",
    icon: CheckCircle2,
    pill: "text-[#0A4730]",
  },
  confirmed: {
    label: "In Progress",
    icon: Clock,
    pill: "text-[#7A5F00]",
  },
  draft: {
    label: "Pending",
    icon: Clock,
    pill: "text-[#7A5F00]",
  },
  failed: {
    label: "Failed",
    icon: XCircle,
    pill: "text-red-700",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    pill: "text-red-700",
  },
};

export function TransactionHistory() {
  const [query, setQuery] = useState("");
  const [isProfileOverlayOpen, setIsProfileOverlayOpen] = useState(false);
  const navigate = useNavigate();

  const { user } = useAuth();
  const { unreadCount } = useNotifications();
  const {
    profile,
    transfers,
    isLoadingTransfers,
    transfersError,
    recipients,
  } = useMe();

  const initials = (user?.full_name || profile?.full_name)
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() ?? "?";

  const recipientsById = (recipients ?? []).reduce((map, r) => {
    map[r.id] = r;
    return map;
  }, {});

  const filtered = (transfers ?? []).filter((t) => {
    const recipient = recipientsById[t.recipient_id];
    const name = recipient?.nickname || recipient?.full_name || "";
    return name.toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="min-h-full bg-transparent pb-24 overflow-y-auto scrollbar-hidden text-left">

      {/* HEADER */}
      <div className="bg-[#042416] md:mx-auto md:max-w-5xl md:mt-6 md:rounded-[40px] rounded-b-[50px] px-6 pt-12 pb-8 shadow-[0_12px_30px_-10px_rgba(4,36,22,0.4)] relative z-10">

        <div className="flex justify-between items-start mb-8">
          <div className="text-[#C0F6B7]">
            <p className="text-[11px] md:text-xs text-white/80 font-medium uppercase tracking-[0.15em] mb-1.5">
              Activity
            </p>
            <h1 className="text-[28px] md:text-4xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Money Movement
            </h1>
            <p className="text-xs md:text-sm text-white/70 mt-2 font-light">
              Money sent across borders
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate("/notifications")}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white transition-transform active:scale-95 border border-white/5"
              aria-label={`${unreadCount} unread notifications`}
            >
              <Bell size={18} strokeWidth={1.5} />
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[#FFB800] text-[#042416] text-[10px] font-bold flex items-center justify-center shadow-sm">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setIsProfileOverlayOpen(true)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFB800] text-[#042416] transition-transform active:scale-95"
            >
              <span className="font-serif text-sm font-bold">
                {initials}
              </span>
            </button>
          </div>
        </div>

        {/* PROFILE OVERLAY */}
        {isProfileOverlayOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
            onClick={() => setIsProfileOverlayOpen(false)}
          >
            <div
              className="w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <ProfileOverlay
                onClose={() => setIsProfileOverlayOpen(false)}
                onActivityPress={() => navigate("/transactionhistory")}
                onSupportPress={() => navigate("/support")}
              />
            </div>
          </div>
        )}

        {/* SEARCH */}
        <div className="relative mt-2">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50" size={16} strokeWidth={1.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="text"
            placeholder="Search transfers"
            className="w-full bg-white/10 border border-white/5 rounded-[16px] py-3.5 pl-11 pr-11 text-white text-[13px] md:text-[14px] placeholder-white/50 shadow-inner focus:outline-none focus:bg-white/20 transition-all"
          />
          <Filter size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60" strokeWidth={1.5} />
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-5xl mx-auto px-5 md:px-0">

        <div className="pt-6 flex items-center justify-between">
          <h2 className="text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
            Recent remittances
          </h2>
        </div>

        {/* LIST */}
        <div className="py-4 space-y-2.5">

          {isLoadingTransfers && (
            [...Array(3)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-4 flex items-center gap-3 border border-[#0a2540]/5 shadow-sm animate-pulse"
              >
                <div className="w-10 h-10 rounded-xl bg-slate-100 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-slate-100 rounded w-1/3" />
                  <div className="h-2.5 bg-slate-100 rounded w-1/4" />
                </div>
              </div>
            ))
          )}

          {transfersError && (
            <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
              Couldn't load your transfer history. Please try again.
            </div>
          )}

          {!isLoadingTransfers && !transfersError && filtered.length === 0 && (
            <div className="text-center py-16">
              <p className="text-sm font-medium text-[#0a2540]/40">
                {query ? "No transfers match your search" : "No transfers yet"}
              </p>
            </div>
          )}

          {!isLoadingTransfers && filtered.map((t) => {
            const recipient = recipientsById[t.recipient_id];
            const name = recipient?.nickname || recipient?.full_name || "Unknown recipient";
            const date = new Date(t.created_at).toLocaleDateString("en-GB", {
              day: "2-digit", month: "short", year: "numeric",
            });
            const config = STATUS_DISPLAY[t.status] ?? STATUS_DISPLAY.draft;
            const StatusIcon = config.icon;
            const reference = t.public_reference || t.id.slice(0, 8).toUpperCase();

            return (
              <div
                key={t.id}
                className="bg-white rounded-2xl border border-[#0a2540]/5 shadow-sm flex items-center overflow-hidden"
              >
                {/* MAIN TAP AREA */}
                <button
                  onClick={() => navigate(`/transaction/${t.id}`)}
                  className="flex-1 flex items-center gap-3 p-3 md:p-4 active:bg-slate-50 transition text-left min-w-0"
                >
                  {/* AVATAR */}
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#E8F5E1] text-[#0A4730] shrink-0">
                    <ArrowUpRight size={16} strokeWidth={2.5} className="rotate-45" />
                  </div>

                  {/* INFO - Aligned with recipient subtext styling */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0a2540] leading-tight truncate">
                      {name}
                    </p>
                    <p className="text-[10px] text-[#0a2540]/40 mt-0.5 flex items-center gap-1.5 font-medium truncate">
                      <span>{reference}</span>
                      <span>·</span>
                      <span>{date}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1 shrink-0">
                        <StatusIcon size={11} strokeWidth={2.5} />
                        {config.label}
                      </span>
                    </p>
                  </div>

                  <ChevronRight size={15} className="text-[#0a2540]/20 shrink-0" />
                </button>

                {/* DIVIDER - Matches recipient card structural separator */}
                <div className="w-px h-8 bg-slate-100 shrink-0" />

                {/* AMOUNT DISPLAY (Mirrors edit action slot dimensions) */}
                <div className="px-4 h-full flex flex-col items-end justify-center shrink-0">
                  <p className="text-xs md:text-sm font-semibold text-[#0a2540] tabular-nums">
                    {t.send_currency} {Number(t.send_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
